import { useNavigate } from 'react-router-dom';
import { useMasterCart, integrationFor } from '@/contexts/MasterCartContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ShoppingCart, Plus, Minus, X, Package, ArrowRight } from 'lucide-react';

const NAVY = '#1B2A4A';

function CartBody({ onProceed }: { onProceed: () => void }) {
  const { groups, removeItem, updateQuantity, itemCount, grandTotal, totalSavings, clearCart } = useMasterCart();

  if (itemCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-muted-foreground gap-3 py-12">
        <Package className="h-16 w-16 opacity-20" />
        <p className="font-medium">Your cart is empty</p>
        <p className="text-sm text-center px-6">Add deals from your dashboard to get started.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-5">
        {groups.map(g => {
          const integ = integrationFor(g.supplierCode);
          return (
            <div key={g.supplierCode} className="space-y-2">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-foreground text-sm">{g.supplierName}</h3>
                <Badge variant="outline" className={`text-[10px] ${integ === 'api' ? 'bg-success/10 text-success border-success/20' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                  {integ === 'api' ? 'API' : 'Manual'}
                </Badge>
              </div>
              <div className="space-y-2">
                {g.items.map(i => (
                  <div key={i.itemId} className="flex items-start gap-2 p-2.5 rounded-lg border bg-card">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-foreground leading-tight">{i.productName}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">R {i.unitPrice.toFixed(2)} each {i.savingPerUnit > 0 && <span className="text-success">· save R {i.savingPerUnit.toFixed(2)}</span>}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(i.itemId, i.quantity - 1)}>
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="text-sm font-semibold w-8 text-center tabular-nums">{i.quantity}</span>
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(i.itemId, i.quantity + 1)}>
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-bold text-sm tabular-nums">R {(i.unitPrice * i.quantity).toFixed(2)}</p>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive mt-1" onClick={() => removeItem(i.itemId)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-xs text-muted-foreground pt-1">
                <span>{g.supplierName} subtotal</span>
                <span className="font-semibold text-foreground tabular-nums">R {g.subtotal.toFixed(2)}</span>
              </div>
            </div>
          );
        })}
      </div>

      <Separator />
      <div className="px-4 py-3 space-y-1.5 bg-muted/20">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Items</span>
          <span className="font-semibold tabular-nums">{itemCount}</span>
        </div>
        <div className="flex justify-between text-base">
          <span className="font-medium">Grand total</span>
          <span className="font-bold tabular-nums">R {grandTotal.toFixed(2)}</span>
        </div>
        {totalSavings > 0 && (
          <div className="flex justify-between text-sm text-success">
            <span>You save</span>
            <span className="font-semibold tabular-nums">R {totalSavings.toFixed(2)}</span>
          </div>
        )}
      </div>
      <div className="p-4 space-y-2 border-t">
        <Button onClick={onProceed} className="w-full text-white hover:opacity-90" style={{ backgroundColor: NAVY }} size="lg">
          Proceed to order <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <Button variant="ghost" className="w-full text-muted-foreground text-xs" onClick={clearCart}>
          Clear cart
        </Button>
      </div>
    </div>
  );
}

export function MasterCartUI() {
  const isMobile = useIsMobile();
  const { isOpen, setIsOpen, itemCount } = useMasterCart();
  const navigate = useNavigate();

  const proceed = () => { setIsOpen(false); navigate('/checkout/confirm'); };

  return (
    <>
      {/* Floating mobile cart button — sits above bottom nav (~64px) */}
      {isMobile && itemCount > 0 && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed right-4 z-40 h-14 w-14 rounded-full text-white shadow-lg flex items-center justify-center hover:opacity-90 transition"
          style={{ backgroundColor: NAVY, bottom: 'calc(env(safe-area-inset-bottom) + 80px)' }}
          aria-label="Open cart"
        >
          <ShoppingCart className="h-6 w-6" />
          <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
            {itemCount}
          </span>
        </button>
      )}

      {/* Mobile: bottom sheet via Drawer */}
      {isMobile ? (
        <Drawer open={isOpen} onOpenChange={setIsOpen}>
          <DrawerContent className="max-h-[85vh]">
            <DrawerHeader className="text-left">
              <DrawerTitle>My cart</DrawerTitle>
            </DrawerHeader>
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              <CartBody onProceed={proceed} />
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetContent side="right" className="w-[320px] sm:max-w-[320px] p-0 flex flex-col">
            <SheetHeader className="p-4 border-b">
              <SheetTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" /> My cart
                {itemCount > 0 && <Badge variant="secondary" className="ml-1">{itemCount}</Badge>}
              </SheetTitle>
            </SheetHeader>
            <CartBody onProceed={proceed} />
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}

export function MasterCartIconButton({ className = '' }: { className?: string }) {
  const { itemCount, setIsOpen } = useMasterCart();
  return (
    <Button variant="ghost" size="sm" className={`relative ${className}`} onClick={() => setIsOpen(true)} aria-label="Open cart">
      <ShoppingCart className="h-5 w-5" />
      {itemCount > 0 && (
        <Badge className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 text-[10px] bg-destructive text-destructive-foreground">
          {itemCount}
        </Badge>
      )}
    </Button>
  );
}
